(() => {
  if (window.Hnv2OverlayController) return;

  const HISTORY_KEY = 'hnv2Overlay';
  const stack = [];
  const knownOverlays = new Map();
  let savedScrollRestoration;
  let scrollRestorationFrame;

  const holdScrollRestoration = () => {
    if (!('scrollRestoration' in window.history)) return;
    if (scrollRestorationFrame) {
      window.cancelAnimationFrame(scrollRestorationFrame);
      scrollRestorationFrame = undefined;
    }
    if (savedScrollRestoration === undefined) savedScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
  };

  const releaseScrollRestoration = () => {
    if (stack.length > 0 || savedScrollRestoration === undefined) return;
    const restoration = savedScrollRestoration;
    scrollRestorationFrame = window.requestAnimationFrame(() => {
      if (stack.length === 0) {
        window.history.scrollRestoration = restoration;
        savedScrollRestoration = undefined;
      }
      scrollRestorationFrame = undefined;
    });
  };

  const entryFor = (overlay) => stack.find((entry) => entry.overlay === overlay);

  const removeEntry = (overlay) => {
    const index = stack.findIndex((entry) => entry.overlay === overlay);
    if (index === -1) return;
    stack.splice(index, 1);
    overlay.closeFromHnv2Controller();
    releaseScrollRestoration();
  };

  const activate = (overlay, token, pushHistory) => {
    if (entryFor(overlay)) return;
    if (stack.length === 0) holdScrollRestoration();
    stack.push({ overlay, token, closing: false });
    knownOverlays.set(token, overlay);

    if (pushHistory) {
      window.history.pushState(
        { ...(window.history.state || {}), [HISTORY_KEY]: token },
        '',
        window.location.href
      );
    }
  };

  const open = (overlay) => {
    const token = `${overlay.id || overlay.localName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    activate(overlay, token, true);
  };

  const requestClose = (overlay) => {
    const entry = entryFor(overlay);
    if (!entry) return false;

    if (window.history.state?.[HISTORY_KEY] === entry.token) {
      if (entry.closing) return true;
      entry.closing = true;
      window.history.back();
    } else {
      removeEntry(overlay);
    }
    return true;
  };

  window.addEventListener('popstate', (event) => {
    const requestedToken = event.state?.[HISTORY_KEY];
    const requestedIndex = requestedToken
      ? stack.findIndex((entry) => entry.token === requestedToken)
      : -1;

    if (requestedIndex >= 0) {
      while (stack.length - 1 > requestedIndex) removeEntry(stack.at(-1).overlay);
      return;
    }

    if (requestedToken) {
      const overlay = knownOverlays.get(requestedToken);
      if (overlay?.isConnected && !overlay.hasAttribute('open')) {
        overlay.openFromHnv2History(requestedToken);
      }
      return;
    }

    while (stack.length > 0) removeEntry(stack.at(-1).overlay);
  });

  window.Hnv2OverlayController = {
    open,
    requestClose,
    restore(overlay, token) {
      activate(overlay, token, false);
    },
  };
})();

class Hnv2ModalDialog extends ModalDialog {
  show(opener) {
    super.show(opener);
    window.Hnv2OverlayController.open(this);
  }

  hide() {
    if (!window.Hnv2OverlayController.requestClose(this)) super.hide();
  }

  closeFromHnv2Controller() {
    super.hide();
  }

  openFromHnv2History(token) {
    super.show(this.openedBy);
    window.Hnv2OverlayController.restore(this, token);
  }
}

if (!customElements.get('product-hnv2-dialog')) {
  customElements.define('product-hnv2-dialog', Hnv2ModalDialog);
}

if (!customElements.get('product-hnv2-media-modal')) {
  customElements.define(
    'product-hnv2-media-modal',
    class ProductHnv2MediaModal extends Hnv2ModalDialog {
      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      openFromHnv2History(token) {
        super.openFromHnv2History(token);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const mediaId = this.openedBy?.getAttribute('data-media-id');
        if (!mediaId) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.dataset.mediaId === mediaId);
        });

        const activeMedia = this.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]`);
        const activeMediaTemplate = activeMedia?.querySelector('template');
        const activeMediaContent = activeMediaTemplate?.content;

        activeMedia?.prepare?.();
        activeMedia?.focus?.({ preventScroll: true });
        if (
          activeMedia?.nodeName === 'DEFERRED-MEDIA' &&
          activeMediaContent?.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }
    }
  );
}

if (!customElements.get('product-hnv2-extras')) {
  customElements.define(
    'product-hnv2-extras',
    class ProductHnv2Extras extends HTMLElement {
      variantChangeUnsubscriber = undefined;
      optionChangeUnsubscriber = undefined;
      pendingSelectionPromise = undefined;
      selectionResolving = false;
      resolutionTimeout = undefined;

      connectedCallback() {
        this.addEventListener('input', this.onQuantityChange);
        this.addEventListener('change', this.onQuantityChange);
        this.addEventListener('change', this.onOptionLabelChange);
        this.addEventListener('change', this.onVariantIdChange);
        this.addEventListener('click', this.onBuyNowClick);
        this.addEventListener('shopify:product:select', this.onProductSelect);
        this.syncQuantityState();
        this.syncPurchasePresentation();
        this.updateSelectionSubtotal();
        this.restoreBackInStockFeedback();

        this.optionChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          ({ data: { event } }) => {
            if (!this.contains(event.target) || window.StandardEvents?.ProductSelectEvent) return;
            this.beginSelectionResolution();
          }
        );

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;

          this.replaceFragment(data.html, 'ProductSelection');
          this.replaceFragment(data.html, 'ProductSelectionFacts');
          this.replaceFragment(data.html, 'ProductKeyDetails');
          this.replaceFragment(data.html, 'ProductTransactionContext');
          this.replaceFragment(data.html, 'ProductDelivery');
          this.replaceFragment(data.html, 'ProductGuarantees');
          this.replaceFragment(data.html, 'ProductBackInStock');
          this.syncBackInStockModal(data.html);
          this.syncModalPresentation(data.html, data.variant);
          this.syncQuantityState(data.html);
          this.syncPurchasePresentation();
          this.updateSelectionSubtotal();
          this.pendingSelectionPromise = undefined;
          this.setSelectionResolving(false);
        });
      }

      disconnectedCallback() {
        this.pendingSelectionPromise = undefined;
        this.removeEventListener('input', this.onQuantityChange);
        this.removeEventListener('change', this.onQuantityChange);
        this.removeEventListener('change', this.onOptionLabelChange);
        this.removeEventListener('change', this.onVariantIdChange);
        this.removeEventListener('click', this.onBuyNowClick);
        this.removeEventListener('shopify:product:select', this.onProductSelect);
        this.optionChangeUnsubscriber?.();
        this.variantChangeUnsubscriber?.();
        clearTimeout(this.resolutionTimeout);
      }

      replaceFragment(html, prefix) {
        const id = `${prefix}-${this.dataset.section}`;
        const current = this.querySelector(`#${id}`) || document.getElementById(id);
        const updated = html.getElementById(id);

        if (!current || !updated) return;
        if (current.isEqualNode(updated)) return;
        current.replaceWith(updated);
      }

      syncBackInStockModal(sourceDocument) {
        const id = `ProductBackInStockModalContent-${this.dataset.section}`;
        const current = document.getElementById(id);
        const updated = sourceDocument.getElementById(id);
        if (!current || !updated || current.isEqualNode(updated)) return;
        current.replaceWith(updated);
      }

      restoreBackInStockFeedback() {
        requestAnimationFrame(() => {
          const modal = document.getElementById(`BackInStockModal-${this.dataset.section}`);
          const feedback = modal?.querySelector('[data-back-in-stock-feedback]');
          const opener = this.querySelector('[data-product-back-in-stock] button');
          if (!feedback || !opener || typeof modal.show !== 'function') return;
          modal.show(opener);
          feedback.focus?.({ preventScroll: true });
        });
      }

      syncModalPresentation(sourceDocument, variant) {
        const currentModal = document.getElementById(`ProductModal-${this.dataset.section}`);
        const updatedModal = sourceDocument.getElementById(`ProductModal-${this.dataset.section}`);
        if (!currentModal || !updatedModal) return;

        const currentContent = currentModal.querySelector('.product-hnv2-media-modal__content');
        const updatedContent = updatedModal.querySelector('.product-hnv2-media-modal__content');
        if (currentContent && updatedContent && !currentContent.isEqualNode(updatedContent)) {
          currentContent.innerHTML = updatedContent.innerHTML;
        }

        this.syncOptionalModalFragment(
          currentModal,
          updatedModal,
          '.product-hnv2-media-modal__navigation',
          Boolean(variant?.featured_media?.id)
        );
        this.syncOptionalModalFragment(
          currentModal,
          updatedModal,
          '.product-hnv2-media-modal__product'
        );
      }

      syncOptionalModalFragment(currentModal, updatedModal, selector, forceReplace = false) {
        const current = currentModal.querySelector(selector);
        const updated = updatedModal.querySelector(selector);

        if (current && !updated) {
          current.remove();
          return;
        }

        if (!current && updated) {
          const productSummary = currentModal.querySelector('.product-hnv2-media-modal__product');
          productSummary?.before(updated);
          return;
        }

        if (current && updated && (forceReplace || !current.isEqualNode(updated))) {
          current.replaceWith(updated);
        }
      }

      syncQuantityState(sourceDocument) {
        const quantityForm = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        if (!quantityForm) return;

        const updatedQuantityForm = sourceDocument?.getElementById?.(`Quantity-Form-${this.dataset.section}`);
        const disabled = (updatedQuantityForm || quantityForm).dataset.quantityDisabled === 'true';

        quantityForm.dataset.quantityDisabled = String(disabled);
        quantityForm.classList.toggle('is-disabled', disabled);
        quantityForm.hidden = disabled;

        const quantityControl = quantityForm.querySelector('quantity-input');
        quantityControl?.classList.toggle('is-disabled', disabled);
        quantityControl?.querySelectorAll('.quantity__button, .quantity__input').forEach((control) => {
          control.toggleAttribute('disabled', disabled);
        });

        if (!disabled && sourceDocument) {
          /*
           * Trade has already copied the selected variant's cart quantity,
           * minimum, maximum and increment. Re-run its own boundary resolver
           * now that those native inputs contain the new variant data.
           */
          this.querySelector('product-info')?.setQuantityBoundries?.();
        }

        if (!disabled) quantityControl?.validateQtyRules?.();
      }

      syncPurchasePresentation({ allowBackInStock = true } = {}) {
        const summary = this.querySelector('[data-selection-summary]');
        const buyActions = this.querySelector('[data-product-buy-actions]');
        const backInStock = this.querySelector('[data-product-back-in-stock]');
        const purchasable = summary?.dataset.purchasable === 'true';

        if (buyActions) buyActions.hidden = !purchasable;
        if (backInStock) backInStock.hidden = purchasable || !allowBackInStock;
      }

      setSelectionResolving(resolving) {
        this.selectionResolving = resolving;
        if (!resolving) clearTimeout(this.resolutionTimeout);

        const order = this.querySelector('.product-hnv2__order');
        const orderCore = this.querySelector('.product-hnv2__order-core');
        const progress = this.querySelector('[data-selection-progress]');
        const buyNow = this.querySelector('[data-product-buy-now]');

        if (resolving) {
          order?.setAttribute('aria-busy', 'true');
          orderCore?.setAttribute('inert', '');
          if (progress) progress.hidden = false;
          if (buyNow) buyNow.disabled = true;
          return;
        }

        order?.removeAttribute('aria-busy');
        orderCore?.removeAttribute('inert');
        if (progress) progress.hidden = true;
        const addButton = this.querySelector('[name="add"]');
        if (buyNow) buyNow.disabled = addButton?.disabled ?? true;
      }

      beginSelectionResolution() {
        clearTimeout(this.resolutionTimeout);
        this.setSelectionResolving(true);
        this.resolutionTimeout = setTimeout(() => {
          if (!this.selectionResolving) return;
          this.pendingSelectionPromise = undefined;
          this.setSelectionResolving(false);
          this.querySelector('product-form')?.handleErrorMessage?.(
            'Unable to update this option. Please try again.'
          );
        }, 10000);
      }

      onProductSelect = (event) => {
        if (!this.contains(event.target) || !event.promise) return;

        const selectionPromise = event.promise;
        this.pendingSelectionPromise = selectionPromise;
        this.beginSelectionResolution();

        selectionPromise
          .then(({ variant }) => {
            if (this.pendingSelectionPromise !== selectionPromise || variant !== null) return;
            this.pendingSelectionPromise = undefined;
            this.showUnavailableCombination(event.selectedOptions || []);
          })
          .catch((error) => {
            if (this.pendingSelectionPromise !== selectionPromise) return;
            /*
             * Trade aborts the preceding fetch after the next standard select
             * event has already installed its promise. Its abort handler can
             * therefore reject the newer promise. Keep that selection pending:
             * variantChange will finish a valid result and the native variant
             * ID change below will finish an unavailable combination.
             */
            if (error?.name === 'AbortError') return;
            this.pendingSelectionPromise = undefined;
            this.setSelectionResolving(false);
            this.querySelector('product-form')?.handleErrorMessage?.(
              error?.message || 'Unable to update this option. Please try again.'
            );
          });
      };

      showUnavailableCombination(selectedOptions) {
        const currentSummary = this.querySelector('[data-selection-summary]');
        const unavailableTemplate = this.querySelector(
          `#ProductSelectionFactsUnavailable-${this.dataset.section}`
        );
        const unavailableSummary = unavailableTemplate?.content?.firstElementChild?.cloneNode(true);
        if (currentSummary && unavailableSummary) currentSummary.replaceWith(unavailableSummary);

        const selectedValues = selectedOptions.map(({ value }) => value).filter(Boolean).join(' · ');
        const selectedVariantCopy = this.querySelector('.product-hnv2__selection-copy span');
        if (selectedVariantCopy && selectedValues) selectedVariantCopy.textContent = selectedValues;
        const stockStatus = this.querySelector('.product-hnv2__stock-status');
        if (stockStatus) {
          stockStatus.textContent = window.variantStrings?.unavailable || 'Unavailable';
          stockStatus.dataset.stockState = 'unavailable-combination';
          stockStatus.classList.remove('is-available');
        }
        const quantityForm = this.querySelector(`#Quantity-Form-${this.dataset.section}`);
        if (quantityForm) {
          quantityForm.dataset.quantityDisabled = 'true';
          quantityForm.classList.add('is-disabled');
          quantityForm.hidden = true;
          const quantityControl = quantityForm.querySelector('quantity-input');
          quantityControl?.classList.add('is-disabled');
          quantityControl?.querySelectorAll('.quantity__button, .quantity__input').forEach((control) => {
            control.disabled = true;
          });
        }

        this.querySelector(`#ProductDelivery-${this.dataset.section}`)?.setAttribute('hidden', '');
        this.syncPurchasePresentation({ allowBackInStock: false });
        this.setSelectionResolving(false);
      }

      onQuantityChange = (event) => {
        if (!event.target.matches('.quantity__input')) return;
        this.updateSelectionSubtotal();
      };

      onOptionLabelChange = (event) => {
        if (!event.target.matches('variant-selects input[type="radio"], variant-selects select')) return;

        const optionInput = event.target.closest('.product-form__input');
        const output = optionInput?.querySelector('[data-selected-option-label]');
        if (output) output.textContent = event.target.value;

        const optionInputs = Array.from(this.querySelectorAll('variant-selects .product-form__input'));
        const optionIndex = optionInputs.indexOf(optionInput);
        if (optionIndex < 0) return;

        const headingSummary = this.querySelector(`[data-product-option-summary="${optionIndex}"]`);
        if (headingSummary) headingSummary.textContent = event.target.value;
      };

      onVariantIdChange = (event) => {
        if (!event.target.matches('product-form input[name="id"]') || event.target.value) return;
        if (!this.selectionResolving) return;

        const selectedOptions = this.querySelector('variant-selects')?.getAllSelectedOptions?.() || [];
        this.pendingSelectionPromise = undefined;
        this.showUnavailableCombination(selectedOptions);
      };

      onBuyNowClick = async (event) => {
        const button = event.target.closest('[data-product-buy-now]');
        if (!button || !this.contains(button) || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const form = button.form;
        const productForm = button.closest('product-form');
        if (!form || !productForm || !form.reportValidity()) return;

        const addButton = productForm.querySelector('[name="add"]');
        const spinner = button.querySelector('.loading__spinner');
        let redirecting = false;
        let releaseLicense = () => {};

        productForm.handleErrorMessage?.();
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
        addButton?.setAttribute('aria-disabled', 'true');
        spinner?.classList.remove('hidden');

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = new FormData(form);
          releaseLicense = await productForm.prepareLicenseAdd(config.body);

          const response = await fetch(`${routes.cart_add_url}`, config);
          const payload = await response.json();
          if (!response.ok || payload.status) {
            throw new Error(payload.description || payload.message || window.cartStrings?.error || 'Unable to continue to checkout.');
          }

          redirecting = true;
          window.location.assign(button.dataset.checkoutUrl);
        } catch (error) {
          productForm.handleErrorMessage?.(error.message);
          productForm.dispatchCartErrorEvent?.(error.message, 'INVALID');
        } finally {
          releaseLicense();
          if (redirecting) return;
          button.disabled = false;
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          addButton?.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }
      };

      resolveSelectionPricing(pricing, selectedQuantity) {
        const baseEachPrice = Number(pricing.baseEachPrice);
        const compareEachPrice = Number(pricing.compareEachPrice);
        const cartQuantity = Math.max(0, Number(pricing.cartQuantity || 0));
        const quantity = Math.max(1, Number(selectedQuantity || 1));
        const priceBreaks = Array.isArray(pricing.priceBreaks) ? pricing.priceBreaks : [];

        if (
          !Number.isFinite(baseEachPrice) ||
          !Number.isFinite(compareEachPrice) ||
          !pricing.currencyCode
        ) {
          return null;
        }

        const effectiveTierQuantity = cartQuantity + quantity;
        let activeMinimumQuantity = 0;
        const activeEachPrice = priceBreaks.reduce((activePrice, priceBreak) => {
          const minimumQuantity = Number(priceBreak.minimumQuantity);
          const breakPrice = Number(priceBreak.price);
          if (
            Number.isFinite(minimumQuantity) &&
            Number.isFinite(breakPrice) &&
            effectiveTierQuantity >= minimumQuantity &&
            minimumQuantity >= activeMinimumQuantity
          ) {
            activeMinimumQuantity = minimumQuantity;
            return breakPrice;
          }
          return activePrice;
        }, baseEachPrice);

        const referenceEachPrice = compareEachPrice > baseEachPrice ? compareEachPrice : baseEachPrice;
        const discountEach = Math.max(0, referenceEachPrice - activeEachPrice);

        return {
          quantity,
          activeEachPrice,
          referenceEachPrice,
          originalTotal: referenceEachPrice * quantity,
          discountTotal: discountEach * quantity,
          discountPercentage:
            referenceEachPrice > 0 ? Math.round((discountEach / referenceEachPrice) * 100) : 0,
          subtotal: activeEachPrice * quantity,
          hasDiscount: discountEach > 0,
          currencyCode: pricing.currencyCode,
          moneyFormat: pricing.moneyFormat,
        };
      }

      updateSelectionSubtotal() {
        const summary = this.querySelector('[data-selection-summary]');
        const subtotal = this.querySelector('[data-selection-subtotal]');
        const quantityOutput = this.querySelector('[data-selection-quantity]');
        const priceEachOutput = this.querySelector('[data-selection-price-each]');
        const originalTotal = this.querySelector('[data-selection-original-total]');
        const originalRow = this.querySelector('[data-selection-original-row]');
        const discount = this.querySelector('[data-selection-discount]');
        const discountPercentage = this.querySelector('[data-selection-discount-percentage]');
        const discountRow = this.querySelector('[data-selection-discount-row]');
        const subtotalRow = this.querySelector('[data-selection-subtotal-row]');
        const quantityInput = this.querySelector('.quantity__input');
        if (!summary || !subtotal) return;
        if (summary.dataset.purchasable !== 'true') {
          if (subtotalRow) subtotalRow.hidden = true;
          return;
        }

        let pricing;
        try {
          pricing = JSON.parse(summary.querySelector('[data-selection-pricing-config]')?.textContent || '{}');
        } catch {
          return;
        }

        const resolved = this.resolveSelectionPricing(pricing, quantityInput?.value);
        if (!resolved) return;

        try {
          const format = (value) =>
            this.formatMoney(value, resolved.moneyFormat, resolved.currencyCode);

          subtotal.textContent = format(resolved.subtotal);
          if (subtotalRow) subtotalRow.hidden = false;

          if (quantityOutput) quantityOutput.textContent = String(resolved.quantity);
          if (priceEachOutput) priceEachOutput.textContent = format(resolved.referenceEachPrice);
          if (originalTotal) originalTotal.textContent = format(resolved.originalTotal);
          if (originalRow) originalRow.hidden = !resolved.hasDiscount;
          if (discount) discount.textContent = `−${format(resolved.discountTotal)}`;
          if (discountPercentage) discountPercentage.textContent = `(${resolved.discountPercentage}%)`;
          if (discountRow) discountRow.hidden = !resolved.hasDiscount;
        } catch {
          // The server-rendered amount remains the safe fallback.
        }
      }

      formatMoney(cents, moneyFormat, currency) {
        const placeholder = moneyFormat?.match(/\{\{\s*(\w+)\s*\}\}/);
        if (placeholder) {
          const formats = {
            amount: [2, ',', '.'],
            amount_no_decimals: [0, ',', '.'],
            amount_with_comma_separator: [2, '.', ','],
            amount_no_decimals_with_comma_separator: [0, '.', ','],
            amount_with_apostrophe_separator: [2, "'", '.'],
            amount_with_space_separator: [2, ' ', ','],
            amount_no_decimals_with_space_separator: [0, ' ', ','],
          };
          const [precision, thousands, decimal] = formats[placeholder[1]] || formats.amount;
          const amount = this.formatWithDelimiters(cents, precision, thousands, decimal);
          return moneyFormat.replace(placeholder[0], amount);
        }

        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(cents / 100);
      }

      formatWithDelimiters(cents, precision, thousands, decimal) {
        const parts = (cents / 100).toFixed(precision).split('.');
        const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
      }
    }
  );
}

if (!customElements.get('product-hnv2-chat')) {
  customElements.define(
    'product-hnv2-chat',
    class ProductHnv2Chat extends HTMLElement {
      responseTimer = undefined;
      audioContext = undefined;
      soundEnabled = true;
      busy = false;

      connectedCallback() {
        this.messages = this.querySelector('[data-chat-messages]');
        this.form = this.querySelector('[data-chat-form]');
        this.input = this.querySelector('[data-chat-input]');
        this.submitButton = this.querySelector('[data-chat-submit]');
        this.soundButton = this.querySelector('[data-chat-sound]');
        this.form?.addEventListener('submit', this.onSubmit);
        this.soundButton?.addEventListener('click', this.onSoundToggle);
        this.querySelectorAll('[data-chat-suggestion]').forEach((button) => {
          button.addEventListener('click', this.onSuggestion);
        });
      }

      disconnectedCallback() {
        clearTimeout(this.responseTimer);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.soundButton?.removeEventListener('click', this.onSoundToggle);
        this.querySelectorAll('[data-chat-suggestion]').forEach((button) => {
          button.removeEventListener('click', this.onSuggestion);
        });
        this.audioContext?.close();
        this.audioContext = undefined;
      }

      onSuggestion = (event) => {
        const button = event.currentTarget;
        this.prepareAudio();
        this.queueExchange(button.textContent.trim(), {
          text: button.dataset.response,
          product: this.productFromDataset(button.dataset),
        });
      };

      onSubmit = (event) => {
        event.preventDefault();
        const question = this.input?.value.trim();
        if (!question || this.busy) return;
        this.prepareAudio();
        this.queueExchange(question, {
          text: this.dataset.staticResponse,
          link: {
            label: this.dataset.staticLinkLabel,
            url: this.dataset.staticLinkUrl,
          },
        });
        this.form.reset();
      };

      onSoundToggle = () => {
        this.soundEnabled = !this.soundEnabled;
        this.soundButton?.setAttribute('aria-pressed', String(this.soundEnabled));
        if (this.soundButton) this.soundButton.textContent = this.soundEnabled ? 'Sound on' : 'Sound off';
        if (this.soundEnabled) this.prepareAudio();
      };

      queueExchange(question, response) {
        if (!this.messages || !question || !response?.text || this.busy) return;
        this.busy = true;
        this.setControlsDisabled(true);

        const userMessage = document.createElement('p');
        userMessage.className = 'product-hnv2-chat__message is-customer';
        userMessage.textContent = question;
        this.messages.append(userMessage, this.createTypingIndicator());
        this.scrollMessages();

        this.responseTimer = window.setTimeout(() => {
          this.querySelector('[data-chat-typing]')?.remove();
          this.appendAssistantMessage(response);
          this.playIncomingSound();
          this.busy = false;
          this.setControlsDisabled(false);
        }, 650);
      }

      /**
       * Render the stable future-service contract:
       * { text, link?: { label, url }, product?: { title, url, image, imageAlt, price, available } }
       */
      appendAssistantMessage(payload) {
        if (!this.messages || !payload?.text) return;
        const assistantMessage = document.createElement('div');
        assistantMessage.className = 'product-hnv2-chat__message is-assistant';
        const text = document.createElement('p');
        text.textContent = payload.text;
        assistantMessage.append(text);

        const link = this.createSafeLink(payload.link);
        if (link) assistantMessage.append(link);

        const productCard = this.createProductCard(payload.product);
        if (productCard) assistantMessage.append(productCard);

        this.messages.append(assistantMessage);
        this.scrollMessages();
      }

      createTypingIndicator() {
        const indicator = document.createElement('div');
        indicator.className = 'product-hnv2-chat__message product-hnv2-chat__typing is-assistant';
        indicator.dataset.chatTyping = '';
        indicator.setAttribute('aria-label', 'Preparing reply');
        for (let index = 0; index < 3; index += 1) {
          const dot = document.createElement('span');
          dot.setAttribute('aria-hidden', 'true');
          indicator.append(dot);
        }
        return indicator;
      }

      createSafeLink(linkData) {
        const safeUrl = this.safeUrl(linkData?.url);
        if (!safeUrl || !linkData?.label) return null;

        const link = document.createElement('a');
        link.className = 'product-hnv2-chat__response-link';
        link.href = safeUrl.href;
        link.textContent = linkData.label;
        if (safeUrl.origin !== window.location.origin) {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }

        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        link.append(arrow);
        return link;
      }

      createProductCard(product) {
        const safeUrl = this.safeUrl(product?.url);
        if (!safeUrl || !product?.title) return null;

        const card = document.createElement('a');
        card.className = 'product-hnv2-chat__product';
        card.href = safeUrl.href;

        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.imageAlt || product.title;
          image.width = 80;
          image.height = 80;
          image.loading = 'lazy';
          card.append(image);
        }

        const content = document.createElement('span');
        content.className = 'product-hnv2-chat__product-content';
        const title = document.createElement('strong');
        title.textContent = product.title;
        content.append(title);

        const meta = document.createElement('span');
        meta.textContent = [product.price, product.available ? 'Available' : 'Sold out'].filter(Boolean).join(' · ');
        content.append(meta);
        card.append(content);

        const arrow = document.createElement('span');
        arrow.className = 'product-hnv2-chat__product-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        card.append(arrow);
        return card;
      }

      productFromDataset(dataset) {
        if (!dataset.productUrl || !dataset.productTitle) return null;
        return {
          title: dataset.productTitle,
          url: dataset.productUrl,
          price: dataset.productPrice,
          available: dataset.productAvailable === 'true',
          image: dataset.productImage,
          imageAlt: dataset.productImageAlt,
        };
      }

      safeUrl(value) {
        if (!value) return null;
        try {
          const url = new URL(value, window.location.origin);
          return ['http:', 'https:'].includes(url.protocol) ? url : null;
        } catch {
          return null;
        }
      }

      setControlsDisabled(disabled) {
        this.submitButton?.toggleAttribute('disabled', disabled);
        this.querySelectorAll('[data-chat-suggestion]').forEach((button) => {
          button.toggleAttribute('disabled', disabled);
        });
      }

      scrollMessages() {
        if (!this.messages) return;
        this.messages.scrollTop = this.messages.scrollHeight;
      }

      prepareAudio() {
        if (!this.soundEnabled || this.audioContext) return;
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) this.audioContext = new AudioContext();
      }

      playIncomingSound() {
        if (!this.soundEnabled || !this.audioContext) return;
        if (this.audioContext.state === 'suspended') this.audioContext.resume();

        const now = this.audioContext.currentTime;
        const gain = this.audioContext.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.035, now + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
        gain.connect(this.audioContext.destination);

        [520, 660].forEach((frequency, index) => {
          const oscillator = this.audioContext.createOscillator();
          oscillator.type = 'sine';
          oscillator.frequency.value = frequency;
          oscillator.connect(gain);
          oscillator.start(now + index * 0.055);
          oscillator.stop(now + 0.22);
        });
      }
    }
  );
}

if (!customElements.get('product-hnv2-voice')) {
  customElements.define(
    'product-hnv2-voice',
    class ProductHnv2Voice extends HTMLElement {
      cues = [];
      abortController = undefined;

      connectedCallback() {
        this.audio = this.querySelector('audio');
        this.controls = this.querySelector('[data-voice-controls]');
        this.playButton = this.querySelector('[data-voice-play]');
        this.progress = this.querySelector('[data-voice-progress]');
        this.caption = this.querySelector('[data-voice-caption]');

        if (!this.audio || !this.controls || !this.playButton || !this.progress) return;

        this.classList.add('is-enhanced');
        this.controls.hidden = false;
        this.playButton.addEventListener('click', this.togglePlayback);
        this.progress.addEventListener('input', this.seek);
        this.audio.addEventListener('loadedmetadata', this.update);
        this.audio.addEventListener('timeupdate', this.update);
        this.audio.addEventListener('play', this.update);
        this.audio.addEventListener('pause', this.update);
        this.audio.addEventListener('ended', this.update);
        this.loadSrt();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.playButton?.removeEventListener('click', this.togglePlayback);
        this.progress?.removeEventListener('input', this.seek);
        this.audio?.removeEventListener('loadedmetadata', this.update);
        this.audio?.removeEventListener('timeupdate', this.update);
        this.audio?.removeEventListener('play', this.update);
        this.audio?.removeEventListener('pause', this.update);
        this.audio?.removeEventListener('ended', this.update);
      }

      async loadSrt() {
        if (!this.dataset.srtUrl || !this.caption) return;

        this.abortController = new AbortController();
        try {
          const response = await fetch(this.dataset.srtUrl, { signal: this.abortController.signal });
          if (!response.ok) return;
          this.cues = this.parseSrt(await response.text());
          this.updateCaption(0);
        } catch (error) {
          if (error.name !== 'AbortError') console.warn('Product voice transcript could not be loaded.');
        }
      }

      parseSrt(source) {
        return source
          .replace(/\r/g, '')
          .trim()
          .split(/\n{2,}/)
          .map((block) => {
            const lines = block.split('\n');
            const timingIndex = lines.findIndex((line) => line.includes('-->'));
            if (timingIndex < 0) return null;

            const [start, end] = lines[timingIndex].split('-->').map((value) => this.parseTimestamp(value.trim()));
            const text = lines
              .slice(timingIndex + 1)
              .join(' ')
              .replace(/<[^>]*>/g, '')
              .trim();

            return Number.isFinite(start) && Number.isFinite(end) && text ? { start, end, text } : null;
          })
          .filter(Boolean);
      }

      parseTimestamp(value) {
        const match = value.match(/(?:(\d+):)?(\d{2}):(\d{2})[,.](\d{3})/);
        if (!match) return Number.NaN;
        return Number(match[1] || 0) * 3600 + Number(match[2]) * 60 + Number(match[3]) + Number(match[4]) / 1000;
      }

      togglePlayback = () => {
        if (this.audio.paused) {
          this.audio.play();
        } else {
          this.audio.pause();
        }
      };

      seek = () => {
        if (!Number.isFinite(this.audio.duration)) return;
        this.audio.currentTime = (Number(this.progress.value) / 100) * this.audio.duration;
      };

      update = () => {
        const duration = Number.isFinite(this.audio.duration) ? this.audio.duration : 0;
        const current = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
        const progress = duration > 0 ? (current / duration) * 100 : 0;

        this.progress.value = progress;
        this.progress.style.setProperty('--product-hnv2-voice-progress', `${progress}%`);
        this.querySelector('[data-voice-current]').textContent = this.formatTime(current);
        this.querySelector('[data-voice-total]').textContent = this.formatTime(duration);
        this.querySelector('[data-voice-duration]').textContent = `· ${this.formatTime(duration)}`;
        this.querySelector('[data-voice-play-icon]').hidden = !this.audio.paused;
        this.querySelector('[data-voice-pause-icon]').hidden = this.audio.paused;
        this.playButton.setAttribute('aria-label', this.audio.paused ? 'Play product explanation' : 'Pause product explanation');
        this.updateCaption(current);
      };

      updateCaption(currentTime) {
        if (!this.caption || this.cues.length === 0) return;
        const activeCue =
          this.cues.find((cue) => currentTime >= cue.start && currentTime < cue.end) ||
          this.cues.find((cue) => cue.start > currentTime) ||
          this.cues[this.cues.length - 1];
        const cueElement = this.caption.querySelector('[data-voice-cue]');
        if (cueElement && cueElement.textContent !== activeCue.text) cueElement.textContent = activeCue.text;
      }

      formatTime(seconds) {
        const rounded = Math.max(0, Math.floor(seconds));
        return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, '0')}`;
      }
    }
  );
}

if (!customElements.get('product-hnv2-modal-controls')) {
  customElements.define(
    'product-hnv2-modal-controls',
    class ProductHnv2ModalControls extends HTMLElement {
      connectedCallback() {
        this.modal = this.closest('product-hnv2-media-modal');
        this.dialog = this.modal.querySelector('.product-hnv2-media-modal__dialog');
        this.media = [...this.modal.querySelector('.product-hnv2-media-modal__content').children].filter(
          (element) => element.hasAttribute('data-media-id')
        );
        this.thumbnails = [...this.querySelectorAll('[data-modal-target]')];
        this.previousButton = this.querySelector('[data-modal-previous]');
        this.nextButton = this.querySelector('[data-modal-next]');
        this.current = this.querySelector('[data-modal-current]');

        this.previousButton?.addEventListener('click', this.showPrevious);
        this.nextButton?.addEventListener('click', this.showNext);
        this.dialog?.addEventListener('pointerup', this.stopPointerup);
        this.modal.addEventListener('keydown', this.onKeydown);
        this.modal.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.modal.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', this.onThumbnailClick));

        this.observer = new MutationObserver(() => {
          if (this.modal.hasAttribute('open')) requestAnimationFrame(() => this.syncFromActiveMedia());
        });
        this.observer.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.previousButton?.removeEventListener('click', this.showPrevious);
        this.nextButton?.removeEventListener('click', this.showNext);
        this.dialog?.removeEventListener('pointerup', this.stopPointerup);
        this.modal?.removeEventListener('keydown', this.onKeydown);
        this.modal?.removeEventListener('touchstart', this.onTouchStart);
        this.modal?.removeEventListener('touchend', this.onTouchEnd);
        this.thumbnails.forEach((thumbnail) => thumbnail.removeEventListener('click', this.onThumbnailClick));
      }

      onThumbnailClick = (event) => {
        this.showMedia(event.currentTarget.dataset.modalTarget);
      };

      showPrevious = () => {
        this.showByOffset(-1);
      };

      showNext = () => {
        this.showByOffset(1);
      };

      stopPointerup = (event) => {
        event.stopPropagation();
      };

      onKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.showByOffset(-1);
        if (event.key === 'ArrowRight') this.showByOffset(1);
      };

      onTouchStart = (event) => {
        this.touchStartX = event.changedTouches[0]?.clientX;
      };

      onTouchEnd = (event) => {
        const touchEndX = event.changedTouches[0]?.clientX;
        if (!Number.isFinite(this.touchStartX) || !Number.isFinite(touchEndX)) return;
        const distance = touchEndX - this.touchStartX;
        if (Math.abs(distance) < 48) return;
        this.showByOffset(distance > 0 ? -1 : 1);
      };

      showByOffset(offset) {
        const activeIndex = Math.max(0, this.media.findIndex((media) => media.classList.contains('active')));
        const targetIndex = (activeIndex + offset + this.media.length) % this.media.length;
        this.showMedia(this.media[targetIndex].dataset.mediaId);
      }

      showMedia(mediaId) {
        const activeMedia = this.media.find((media) => media.dataset.mediaId === String(mediaId));
        if (!activeMedia) return;

        window.pauseAllMedia();
        this.media.forEach((media) => media.classList.toggle('active', media === activeMedia));
        activeMedia.prepare?.();
        this.syncFromActiveMedia();
        activeMedia.focus?.({ preventScroll: true });
      }

      syncFromActiveMedia() {
        const activeMedia = this.media.find((media) => media.classList.contains('active')) || this.media[0];
        if (!activeMedia) return;
        const activeIndex = this.media.indexOf(activeMedia);

        if (this.current) this.current.textContent = String(activeIndex + 1);
        this.thumbnails.forEach((thumbnail) => {
          const isCurrent = thumbnail.dataset.modalTarget === activeMedia.dataset.mediaId;
          if (isCurrent) {
            thumbnail.setAttribute('aria-current', 'true');
          } else {
            thumbnail.removeAttribute('aria-current');
          }
          if (isCurrent) thumbnail.scrollIntoView({ block: 'nearest', inline: 'center' });
        });
      }
    }
  );
}
